/**
 * Sprint S13.1.B.2 — Step 6: preferred_training_time + typical_session_length_min.
 */
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { FieldSelect } from "@/components/onboarding/fields/FieldSelect";
import { FieldRangeSlider } from "@/components/onboarding/fields/FieldRangeSlider";


const TIME_VALUES = [
  "morning_5_9",
  "morning_6_10",
  "afternoon_12_17",
  "evening_17_21",
  "split_am_pm",
  "flexible",
] as const;


const schema = z.object({
  preferred_training_time: z.enum(TIME_VALUES),
});

type ScheduleForm = z.infer<typeof schema>;


export function Step6Schedule() {
  const { t } = useTranslation();
  const data = useOnboardingStore((s) => s.data);
  const setData = useOnboardingStore((s) => s.setData);
  const setField = useOnboardingStore((s) => s.setField);
  const nextStep = useOnboardingStore((s) => s.nextStep);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ScheduleForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      preferred_training_time: data.preferred_training_time,
    },
  });

  const onSubmit = (values: ScheduleForm) => {
    setData(values);
    nextStep();
  };

  const timeOptions = TIME_VALUES.map((v) => ({
    value: v,
    label: t(`onboarding.step.schedule.time.${v}`),
  }));

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <h2
          id="step-heading"
          tabIndex={-1}
          className="mb-2 text-xl font-semibold text-navy-deep sm:text-2xl"
        >
          {t("onboarding.step.schedule.title")}
        </h2>
        <p className="text-[13px] text-taupe sm:text-[14px]">
          {t("onboarding.step.schedule.subtitle")}
        </p>
      </div>

      <FieldSelect
        label={t("onboarding.step.schedule.time_label")}
        hint={t("onboarding.step.schedule.time_hint")}
        options={timeOptions}
        registration={register("preferred_training_time")}
        error={errors.preferred_training_time?.message}
      />

      <FieldRangeSlider
        label={t("onboarding.step.schedule.session_length_label")}
        hint={t("onboarding.step.schedule.session_length_hint")}
        value={data.typical_session_length_min}
        onChange={(vals) => setField("typical_session_length_min", vals)}
        unit={t("onboarding.step.schedule.unit_min")}
      />

      <div className="pt-2">
        <Button type="submit" data-onboarding-next className="w-full sm:w-auto">
          {t("onboarding.nav.next")}
        </Button>
      </div>
    </form>
  );
}
